import { getClinicalTaskAssignee } from '@bariendo/lib/clinical-tasks';
import {
  getProviderRoleLabel,
  PROVIDER_ROLE_OPTIONS,
  PROVIDER_TASK_PERFORMER_ROLES,
  ProviderRole,
} from '@bariendo/lib/constants';
import { resolveId } from '@medplum/core';
import { Task } from '@medplum/fhirtypes';

/** The performer role of a role-assigned clinical task, if any. */
export function getClinicalTaskPerformerRole(task: Task): ProviderRole | undefined {
  return task.performerType?.[0]?.coding?.[0]?.code as ProviderRole | undefined;
}

/** "Assigned to" display for a clinical task: the person's name, or the role's label. */
export function getClinicalTaskAssignmentLabel(task: Task): string {
  const assignee = getClinicalTaskAssignee(task);
  if (assignee) {
    return assignee.display ?? assignee.reference ?? '';
  }
  return getProviderRoleLabel(getClinicalTaskPerformerRole(task));
}

/** Team groupings for the admin Task log's outstanding view. */
export const TASK_TEAMS = [
  { label: 'Intake', roles: [ProviderRole.PATIENT_NAVIGATOR, ProviderRole.PATIENT_ACCESS] },
  // "Care coordinators" = the Care Coordinator (CC, formerly PC) role.
  { label: 'Care operations', roles: [ProviderRole.PATIENT_COORDINATOR, ProviderRole.CARE_MANAGER] },
  { label: 'Care team', roles: [ProviderRole.NURSE_PRACTITIONER, ProviderRole.REGISTERED_DIETITIAN] },
] as const;

export type TaskTeamLabel = (typeof TASK_TEAMS)[number]['label'] | 'Other';

/** The Task-log team a role belongs to; roles outside the three teams (admin, MD, …) land in Other. */
export function getTaskTeamForRole(role: ProviderRole | undefined): TaskTeamLabel {
  for (const team of TASK_TEAMS) {
    if (role && (team.roles as readonly ProviderRole[]).includes(role)) {
      return team.label;
    }
  }
  return 'Other';
}

/**
 * The team a task belongs to: role-assigned tasks by their performer role;
 * individually assigned tasks by the ASSIGNEE'S primary role (resolved via
 * the practitioner directory), so a task assigned to a specific NP still
 * shows under Care team.
 */
export function getClinicalTaskTeamLabel(
  task: Task,
  roleByPractitionerId: ReadonlyMap<string, ProviderRole>
): TaskTeamLabel {
  const assignee = getClinicalTaskAssignee(task);
  if (assignee) {
    const assigneeId = assignee.reference ? resolveId(assignee) : undefined;
    return getTaskTeamForRole(assigneeId ? roleByPractitionerId.get(assigneeId) : undefined);
  }
  const roleCode = task.performerType?.[0]?.coding?.[0]?.code as ProviderRole | undefined;
  return getTaskTeamForRole(roleCode);
}

/**
 * Everyone taggable as a task assignee: the full provider directory except
 * read-only accounts (the auditor, the research fellow, marketing) and Finance,
 * who cannot act on a task — work tagged to them would vanish (BAR-2648).
 */
export const TASK_TAGGABLE_ROLES: ProviderRole[] = PROVIDER_ROLE_OPTIONS.map((option) => option.value).filter(
  (role) =>
    role !== ProviderRole.EMERSON_QUALITY_AUDITOR &&
    role !== ProviderRole.FINANCE &&
    role !== ProviderRole.RESEARCH_FELLOW &&
    role !== ProviderRole.MARKETING
);

export const TASK_PERFORMER_SELECT_DATA = PROVIDER_TASK_PERFORMER_ROLES.map(
  (role) => PROVIDER_ROLE_OPTIONS.find((o) => o.value === role)!
).filter(Boolean);

export const TASK_PRIORITY_SELECT_DATA = [
  { value: 'routine', label: 'Routine' },
  { value: 'high', label: 'High priority' },
] as const;
